import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchHackathonSurveys,
  deleteHackathonSurvey,
  exportHackathonSurveyCsv,
} from '../../api/client';
import formatDate from '../../utils/formatDate';
import './HackathonSurveyList.css';

const BACKGROUND_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'student', label: 'Student' },
  { value: 'employed', label: 'Employed' },
  { value: 'entrepreneur', label: 'Entrepreneur' },
  { value: 'other', label: 'Other' },
];

const FACILITATOR_SUPPORT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'very_poor', label: 'Very Poor' },
  { value: 'poor', label: 'Poor' },
  { value: 'good', label: 'Good' },
  { value: 'very_good', label: 'Very Good' },
  { value: 'outstanding', label: 'Outstanding' },
];

function ratingBadgeClass(value) {
  if (value == null) return '';
  if (value >= 8) return 'badge-positive';
  if (value >= 5) return 'badge-pending';
  return 'badge-negative';
}

function facilitatorBadgeClass(value) {
  if (value === 'outstanding' || value === 'very_good') return 'badge-positive';
  if (value === 'good') return 'badge-pending';
  if (value === 'poor' || value === 'very_poor') return 'badge-negative';
  return 'badge-neutral';
}

function outcomeBadgeClass(outcome) {
  if (outcome === 'completed') return 'badge-positive';
  if (outcome === 'partially_completed') return 'badge-pending';
  return 'badge-negative';
}

function humanize(value) {
  if (!value) return '—';
  return value.replace(/_/g, ' ');
}

export default function HackathonSurveyList() {
  const [surveys, setSurveys] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [skip, setSkip] = useState(0);
  const [backgroundFilter, setBackgroundFilter] = useState('');
  const [facilitatorFilter, setFacilitatorFilter] = useState('');
  const limit = 20;

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit, skip };
      if (backgroundFilter) params.background = backgroundFilter;
      if (facilitatorFilter) params.facilitator_support = facilitatorFilter;
      const res = await fetchHackathonSurveys(params);
      setSurveys(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip, backgroundFilter, facilitatorFilter]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (confirmId === id) {
      try {
        await deleteHackathonSurvey(id);
        setConfirmId(null);
        loadSurveys();
      } catch (err) {
        setError(err.message);
      }
    } else {
      setConfirmId(id);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmId(null);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await exportHackathonSurveyCsv({
        background: backgroundFilter,
        facilitator_support: facilitatorFilter,
      });
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading && surveys.length === 0) {
    return <div className="loading">Loading hackathon surveys...</div>;
  }

  if (error) {
    return <div className="error-message">Failed to load hackathon surveys: {error}</div>;
  }

  const hasMore = skip + limit < total;
  const hasPrev = skip > 0;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Hackathon Survey Responses</h3>
        <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
      {exportError && (
        <div className="error-message" style={{ marginBottom: '0.5rem' }}>
          Export failed: {exportError}
        </div>
      )}
      <div className="hackathon-survey-filter">
        <label htmlFor="hackathon-background-filter">Background:</label>
        <select
          id="hackathon-background-filter"
          value={backgroundFilter}
          onChange={(e) => { setBackgroundFilter(e.target.value); setSkip(0); }}
        >
          {BACKGROUND_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>{o.label}</option>
          ))}
        </select>
        <label htmlFor="hackathon-facilitator-filter" style={{ marginLeft: '0.75rem' }}>Facilitator support:</label>
        <select
          id="hackathon-facilitator-filter"
          value={facilitatorFilter}
          onChange={(e) => { setFacilitatorFilter(e.target.value); setSkip(0); }}
        >
          {FACILITATOR_SUPPORT_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {surveys.length === 0 ? (
        <p>No hackathon survey responses yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Phone</th>
              <th>Experience</th>
              <th>Background</th>
              <th>Team</th>
              <th>Facilitator</th>
              <th>Outcome</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {surveys.map((s) => (
              <React.Fragment key={s._id}>
                <tr onClick={() => toggleExpand(s._id)} style={{ cursor: 'pointer' }}>
                  <td>{formatDate(s.created_at)}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {s.phone_number || 'N/A'}
                  </td>
                  <td>
                    {s.overall_experience_rating != null ? (
                      <span className={`badge ${ratingBadgeClass(s.overall_experience_rating)}`}>
                        {s.overall_experience_rating} / 10
                      </span>
                    ) : '—'}
                  </td>
                  <td>{s.background ? (
                    <span className="badge badge-neutral">{humanize(s.background)}</span>
                  ) : '—'}</td>
                  <td>
                    {s.team_rating != null ? (
                      <span className={`badge ${ratingBadgeClass(s.team_rating)}`}>
                        {s.team_rating} / 10
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    {s.facilitator_support ? (
                      <span className={`badge ${facilitatorBadgeClass(s.facilitator_support)}`}>
                        {humanize(s.facilitator_support)}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <span className={`badge ${outcomeBadgeClass(s.call_outcome)}`}>
                      {humanize(s.call_outcome || 'unknown')}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    {confirmId === s._id ? (
                      <>
                        <button className="btn-danger-sm" onClick={(e) => handleDelete(e, s._id)}>Confirm</button>
                        {' '}
                        <button className="btn-secondary-sm" onClick={handleCancelDelete}>Cancel</button>
                      </>
                    ) : (
                      <button className="btn-icon" onClick={(e) => handleDelete(e, s._id)} title="Delete">&#128465;</button>
                    )}
                  </td>
                </tr>
                {expandedId === s._id && (
                  <tr className="hackathon-survey-detail-row">
                    <td colSpan={8}>
                      <div className="hackathon-survey-details">
                        <div className="hackathon-survey-meta">
                          <span className="detail-label">Call ID:</span>{' '}
                          <span className="detail-value">{s.call_id || 'N/A'}</span>
                        </div>

                        {s.experience_highlight && (
                          <div className="hackathon-survey-section">
                            <div className="hackathon-survey-section-label">What stood out most</div>
                            <div className="hackathon-survey-text">{s.experience_highlight}</div>
                          </div>
                        )}

                        {s.background_detail && (
                          <div className="hackathon-survey-section">
                            <div className="hackathon-survey-section-label">Background detail</div>
                            <div>{s.background_detail}</div>
                          </div>
                        )}

                        {s.team_rating_reason && (
                          <div className="hackathon-survey-section">
                            <div className="hackathon-survey-section-label">Team rating reason</div>
                            <div className="hackathon-survey-text">{s.team_rating_reason}</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {(hasPrev || hasMore) && (
        <div className="submission-pagination">
          <button
            className="btn-secondary"
            disabled={!hasPrev}
            onClick={() => setSkip((prev) => Math.max(0, prev - limit))}
          >
            Previous
          </button>
          <span className="pagination-info">
            {total === 0 ? 0 : skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            className="btn-secondary"
            disabled={!hasMore}
            onClick={() => setSkip((prev) => prev + limit)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
